import { useState } from "react";

/** The brand mark: watch·party with the pulsing purple dot. */
export function Wordmark({ size = "base" }: { size?: "base" | "lg" }) {
  return (
    <span
      className={`display inline-flex items-baseline tracking-tight text-fg ${
        size === "lg" ? "text-3xl sm:text-4xl" : "text-[17px]"
      }`}
    >
      watch
      <span
        className={`pulse-dot mx-[1px] inline-block rounded-full bg-accent ${
          size === "lg" ? "h-2 w-2 translate-y-[-2px]" : "h-[5px] w-[5px] translate-y-[-1px]"
        }`}
      />
      party
    </span>
  );
}

export function LiveBadge({ className = "" }: { className?: string }) {
  return (
    <span
      className={`chamfer-sm inline-flex items-center gap-1.5 bg-ok/10 px-2 py-0.5 font-mono text-[10.5px] font-medium uppercase tracking-wider text-ok ${className}`}
    >
      <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-ok" />
      Live
    </span>
  );
}

export function StateChip({
  tone,
  children,
  pulse = false,
}: {
  tone: "ok" | "warn" | "err" | "accent" | "dim";
  children: React.ReactNode;
  pulse?: boolean;
}) {
  const tones: Record<string, string> = {
    ok: "bg-ok/10 text-ok",
    warn: "bg-warn/10 text-warn",
    err: "bg-err/10 text-err",
    accent: "bg-accent/10 text-accent",
    dim: "bg-raised text-dim",
  };
  const dots: Record<string, string> = {
    ok: "bg-ok",
    warn: "bg-warn",
    err: "bg-err",
    accent: "bg-accent",
    dim: "bg-faint",
  };
  return (
    <span
      className={`chamfer-sm inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10.5px] font-medium uppercase tracking-wider ${tones[tone]}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dots[tone]} ${pulse ? "pulse-dot" : ""}`} />
      {children}
    </span>
  );
}

export function CopyButton({
  text,
  label,
  onCopy,
}: {
  text: string;
  label?: string;
  onCopy?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async e => {
        e.stopPropagation();
        e.preventDefault();
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          onCopy?.();
          setTimeout(() => setCopied(false), 1400);
        } catch {
          // clipboard unavailable — nothing useful to show
        }
      }}
      className={`chamfer-sm shrink-0 px-2 py-1 font-mono text-[10.5px] uppercase tracking-wider transition ${
        copied
          ? "bg-ok/15 text-ok"
          : "bg-accent/10 text-accent hover:bg-accent/20"
      }`}
      title="copy to clipboard"
    >
      {copied ? "copied" : (label ?? "copy")}
    </button>
  );
}

/** Section kicker with the brand dot. */
export function SectionHeader({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="display flex items-center gap-2 text-[17px] text-fg">
          <span className="h-1.5 w-1.5 rounded-full bg-accent" />
          {title}
        </h2>
        {hint && <p className="mt-0.5 text-xs text-faint">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="px-5 py-10 text-center">
      <div className="relative mx-auto h-0.5 w-32 overflow-hidden rounded-full bg-border">
        <span className="sweep-bar absolute inset-y-0 w-1/4 rounded-full bg-accent/70" />
      </div>
      <p className="mt-3 font-mono text-[11px] text-faint">{label}</p>
    </div>
  );
}
